import { useState, useEffect, useMemo, useCallback } from "react";
import { Printer, ClipboardList, X } from 'lucide-react';
import {
  getNominas,
  procesarNomina,
  getEmpleadosActivos,
  getConfiguracion,
  actualizarConfiguracion,
} from "../../../api/nominaService";

// ─── PALETA PRODOMINICANA ─────────────────────────────────────────────────────
const C = {
  primary:      "#00829B",   // aguamarina ProDominicana
  primaryDark:  "#00005A",   // azul navy ProDominicana
  primaryLight: "#E0F4F8",   // aguamarina muy claro
  gold:         "#C8973A",   // dorado acento (se mantiene)
  goldLight:    "#FDF3E3",
  danger:       "#B03A2E",
  dangerLight:  "#FDEDEC",
  warning:      "#B7950B",
  warningLight: "#FEF9E7",
  success:      "#1E8449",
  successLight: "#EAFAF1",
  neutral:      "#2E4057",
  neutralLight: "#F4F6F7",
  border:       "#CDD4D9",
  textMain:     "#1A252F",
  textSub:      "#5D6D7E",
  white:        "#FFFFFF",
};

// ─── UTILIDADES ───────────────────────────────────────────────────────────────
const FMT  = (n) => new Intl.NumberFormat("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 }).format(n || 0);
const PCT  = (n) => `${(+n || 0).toFixed(2)}%`;
const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
               "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

// Helper: nombre completo — el backend devuelve 'nombre'+'apellidos' (con s)
// o 'NOMBRE'+'APELLIDO' según el origen (empleados activos vs nómina procesada)
const NOM = (e) => [
  e.nombre || e.NOMBRE || "",
  e.apellidos || e.APELLIDO || "",  // apellidos (con s) viene de getEmpleadosActivos
].filter(Boolean).join(" ").trim();

// ─── ESCALAS ISR DGII (fallback si la API aún no respondió) ──────────────────
const DEFAULT_ESCALAS_ISR = [
  { LimiteInferior: 0,         LimiteSuperior: 34685.00,     Porcentaje: 0  },
  { LimiteInferior: 34685.01,  LimiteSuperior: 52027.42,     Porcentaje: 15 },
  { LimiteInferior: 52027.43,  LimiteSuperior: 72260.25,     Porcentaje: 20 },
  { LimiteInferior: 72260.26,  LimiteSuperior: 999999999.00, Porcentaje: 25 },
];

// ─── ISR PURAMENTE PROGRESIVO ─────────────────────────────────────────────────
// Verificado con boleta real CEI-RD: salario 75,000
//   Base ISR = 75,000 - AFP(2,152.50) - SFS(2,280) = 70,567.50
//   Tramo 2: (52,027.42 - 34,685.01) × 15% = 2,601.36
//   Tramo 3: (70,567.50 - 52,027.43) × 20% = 3,708.01
//   Total ISR = 6,309.37 ≈ 6,309.34 ✓
//   NOTA: El MontoFijo de la tabla DGII es referencial, NO se suma al cálculo.
function calcularISR(base, escalas) {
  let total = 0;
  const tramos = [];
  const sorted = [...escalas].sort((a, b) => (a.LimiteInferior||0) - (b.LimiteInferior||0));
  for (const e of sorted) {
    const inf  = e.LimiteInferior ?? 0;
    const sup  = e.LimiteSuperior ?? 999_999_999;
    const tasa = (e.Porcentaje ?? 0) / 100;
    if (base <= inf) { tramos.push({ label: e.Nombre||`Tramo`, tasa: e.Porcentaje||0, monto: 0 }); continue; }
    const aplicable = Math.min(base, sup) - inf;
    const monto     = aplicable * tasa;
    total += monto;
    tramos.push({ label: e.Nombre||`Tramo`, tasa: e.Porcentaje||0, aplicable, monto });
  }
  return { total, tramos };
}

// ─── CÁLCULO COMPLETO DE NÓMINA ───────────────────────────────────────────────
function calcularNomina(emp, config) {
  const salario = +(emp.salarioBase ?? emp.Salario ?? emp.SalarioBase ?? 0);

  // Deducciones TSS
  const cfgAfp    = config.deducciones?.find(c => c.Codigo === "TSS_AFP") || { Porcentaje: 2.87 };
  const cfgSfs    = config.deducciones?.find(c => c.Codigo === "TSS_SFS") || { Porcentaje: 3.04 };
  // Savica: monto fijo mensual (RD$25.00 según boleta real)
  const cfgSavica = config.deducciones?.find(c => c.Codigo === "SAVICA")  || { Porcentaje: 25 };

  const afp    = salario * (cfgAfp.Porcentaje    / 100);
  const sfs    = salario * (cfgSfs.Porcentaje    / 100);
  const savica = +(cfgSavica.Porcentaje ?? 25);   // monto fijo, no porcentaje

  const totalTSS = afp + sfs + savica;

  // Base ISR excluye Savica (verificado con boleta real)
  const baseISR = salario - afp - sfs;
  const escalas = (config.escalasISR && config.escalasISR.length > 0)
    ? config.escalasISR
    : DEFAULT_ESCALAS_ISR;
  const { total: totalISR, tramos } = calcularISR(baseISR, escalas);

  const salarioNeto = salario - totalTSS - totalISR;

  // Aportes patronales (referencia interna, no afectan neto empleado)
  const cfgAfpP    = config.aportesPatronales?.find(c => c.Codigo === "AFP_PATRONAL")    || { Porcentaje: 7.10 };
  const cfgSfsP    = config.aportesPatronales?.find(c => c.Codigo === "SFS_PATRONAL")    || { Porcentaje: 7.09 };
  const cfgRiesgo  = config.aportesPatronales?.find(c => c.Codigo === "RIESGO_LABORAL")  || { Porcentaje: 1.10 };
  const cfgInfotep = config.aportesPatronales?.find(c => c.Codigo === "INFOTEP")         || { Porcentaje: 0.50 };

  const patronalAfp    = salario * (cfgAfpP.Porcentaje    / 100);
  const patronalSfs    = salario * (cfgSfsP.Porcentaje    / 100);
  const patronalRiesgo = salario * (cfgRiesgo.Porcentaje  / 100);
  const infotep        = salario * (cfgInfotep.Porcentaje / 100);
  const totalPatronal  = patronalAfp + patronalSfs + patronalRiesgo + infotep;

  return { salario, afp, sfs, savica, totalTSS, baseISR,
           totalISR, tramos, salarioNeto,
           patronalAfp, patronalSfs, patronalRiesgo, infotep, totalPatronal };
}

// ─── ESTILOS ──────────────────────────────────────────────────────────────────
const S = {
  page:     { fontFamily:"'Segoe UI',system-ui,sans-serif", background:"#EEF2F5", minHeight:"100vh", color:C.textMain },
  header:   { background:C.primaryDark, padding:"1rem 2rem", display:"flex", alignItems:"center", justifyContent:"space-between", boxShadow:"0 2px 10px rgba(0,0,0,0.2)" },
  tabs:     { display:"flex", background:C.white, borderBottom:`2px solid ${C.border}`, padding:"0 2rem" },
  tab:      (a) => ({ padding:"0.85rem 1.5rem", border:"none", background:"none", fontSize:"0.85rem", fontWeight:600, cursor:"pointer", color:a?C.primary:C.textSub, borderBottom:a?`2.5px solid ${C.primary}`:"2.5px solid transparent", marginBottom:"-2px", transition:"color 0.15s" }),
  body:     { padding:"1.5rem 2rem" },
  card:     { background:C.white, borderRadius:"10px", boxShadow:"0 1px 5px rgba(0,0,0,0.07)", padding:"1.5rem" },
  grid4:    { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:"1rem", marginBottom:"1.5rem" },
  stat:     (c) => ({ background:C.white, borderRadius:"10px", padding:"1.1rem 1.4rem", borderLeft:`4px solid ${c}`, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }),
  tbl:      { width:"100%", borderCollapse:"collapse", fontSize:"0.83rem" },
  th:       { background:C.primaryDark, color:C.white, padding:"10px 13px", textAlign:"left",  fontWeight:600, fontSize:"0.76rem", textTransform:"uppercase", letterSpacing:"0.04em", whiteSpace:"nowrap" },
  thR:      { background:C.primaryDark, color:C.white, padding:"10px 13px", textAlign:"right", fontWeight:600, fontSize:"0.76rem", textTransform:"uppercase", letterSpacing:"0.04em", whiteSpace:"nowrap" },
  td:       { padding:"9px 13px", borderBottom:`1px solid ${C.neutralLight}`, color:C.neutral, whiteSpace:"nowrap" },
  tdR:      { padding:"9px 13px", borderBottom:`1px solid ${C.neutralLight}`, textAlign:"right", fontWeight:500, whiteSpace:"nowrap", color:C.neutral },
  tdB:      { padding:"9px 13px", borderBottom:`1px solid ${C.neutralLight}`, color:C.primary,  textAlign:"right", fontWeight:700, whiteSpace:"nowrap" },
  trAlt:    { background:"#F8FAFA" },
  btn:      (bg, outline) => ({ padding:"0.55rem 1.2rem", borderRadius:"7px", border:outline?`1.5px solid ${bg}`:"none", background:outline?"transparent":bg, color:outline?bg:C.white, fontWeight:600, fontSize:"0.82rem", cursor:"pointer", display:"flex", alignItems:"center", gap:"0.4rem" }),
  label:    { fontSize:"0.79rem", fontWeight:600, color:C.textSub, marginBottom:"4px", display:"block" },
  input:    { width:"100%", padding:"0.5rem 0.75rem", border:`1px solid ${C.border}`, borderRadius:"7px", fontSize:"0.875rem", color:C.textMain, background:C.white, outline:"none", boxSizing:"border-box" },
  inputR:   { width:"100%", padding:"0.5rem 0.75rem", border:`1px solid ${C.border}`, borderRadius:"7px", fontSize:"0.875rem", color:C.textMain, background:C.white, outline:"none", boxSizing:"border-box", textAlign:"right" },
  secTitle: { fontSize:"0.95rem", fontWeight:700, color:C.primary, marginBottom:"0.9rem", paddingBottom:"0.5rem", borderBottom:`2px solid ${C.primaryLight}` },
  overlay:  { position:"fixed", inset:0, background:"rgba(0,0,0,0.52)", display:"flex", alignItems:"flex-start", justifyContent:"center", zIndex:1000, overflowY:"auto", padding:"2rem 1rem" },
  modal:    { background:C.white, borderRadius:"12px", boxShadow:"0 20px 60px rgba(0,0,0,0.25)", width:"100%", maxWidth:"820px", margin:"auto" },
  mHead:    { background:C.primaryDark, color:C.white, padding:"1rem 1.5rem", borderRadius:"12px 12px 0 0", display:"flex", alignItems:"center", justifyContent:"space-between" },
  closeX:   { background:"rgba(255,255,255,0.15)", border:"none", color:C.white, width:30, height:30, borderRadius:"50%", cursor:"pointer", fontSize:"1rem", display:"flex", alignItems:"center", justifyContent:"center" },
  badge:    (c, bg) => ({ padding:"3px 9px", borderRadius:"20px", fontSize:"0.72rem", fontWeight:600, color:c, background:bg, display:"inline-block", whiteSpace:"nowrap" }),
};

function Loading() { return <div style={{ textAlign:"center", padding:"3rem", color:C.textSub }}>Cargando...</div>; }
function ErrMsg({ m }) { return <div style={{ background:C.dangerLight, border:`1px solid ${C.danger}`, borderRadius:"8px", padding:"1rem", color:C.danger, fontSize:"0.875rem" }}>{m}</div>; }

// ─── BOLETA — FORMATO CEI-RD ──────────────────────────────────────────────────
// Estructura idéntica al comprobante oficial:
//   Encabezado | Datos generales | Tabla Concepto/Unidad/Ingresos/Deducciones | Totales
function Boleta({ emp, calc, periodo }) {
  const mes2 = String(periodo.mes).padStart(2, "0");
  const ult   = new Date(periodo.anio, periodo.mes, 0).getDate();
  const periodoStr = `${periodo.anio}-${mes2}`;
  const desdStr    = `01-${mes2}-${periodo.anio}`;
  const hastaStr   = `${String(ult).padStart(2,"0")}-${mes2}-${periodo.anio}`;
  const nombre     = NOM(emp);
  const cedula     = emp.cedula || emp.CEDULA || "";
  const cargo      = emp.cargo  || emp.CARGO  || "";
  const seccion    = emp.departamento || emp.Departamento || emp.DEPARTAMENTO || "";
  const ingreso    = (emp.fechaIngreso || emp.FECHAINGRESO || "").slice(0,10).split("-").reverse().join("-");
  const codigo     = String(emp.id || emp.EmpleadoID || "").padStart(10, "0");

  // Estilos internos de la boleta (typewriter feel, como el original)
  const bdr   = { border:"1px solid #B0BEC5" };
  const cellL = { padding:"4px 8px", fontSize:"0.77rem", color:C.textMain };
  const cellS = { padding:"4px 8px", fontSize:"0.77rem", color:C.textSub };
  const cellR = { padding:"4px 8px", fontSize:"0.77rem", textAlign:"right" };
  const hdr   = { padding:"4px 8px", fontSize:"0.73rem", fontWeight:700, color:C.textSub, background:"#F4F6F7", textTransform:"uppercase", letterSpacing:"0.04em" };

  return (
    <div style={{ border:`1px solid ${C.border}`, borderRadius:"4px", marginBottom:"1.5rem", pageBreakInside:"avoid", background:C.white, fontFamily:"'Courier New',Courier,monospace" }}>

      {/* Encabezado institucional */}
      <div style={{ background:C.primaryDark, padding:"0.6rem 1rem", display:"grid", gridTemplateColumns:"1fr auto" }}>
        <div>
          <div style={{ color:C.white, fontWeight:700, fontSize:"0.82rem", letterSpacing:"0.3px" }}>
            CENTRO DE EXPORTACION E INVERSION DE LA R.D. (CEI-RD)
          </div>
          <div style={{ color:"rgba(255,255,255,0.6)", fontSize:"0.7rem", marginTop:"1px" }}>ProDominicana</div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ color:C.gold, fontWeight:700, fontSize:"0.78rem" }}>Comprobante de Pago</div>
          <div style={{ color:"rgba(255,255,255,0.6)", fontSize:"0.68rem", marginTop:"1px" }}>
            {new Date().toLocaleDateString("es-DO")}
          </div>
        </div>
      </div>

      {/* Datos generales — 2 columnas como el original */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", borderBottom:`1px solid ${C.border}` }}>
        {/* Columna izquierda */}
        <div style={{ padding:"0.6rem 0.9rem", borderRight:`1px solid ${C.border}` }}>
          <div style={{ fontWeight:700, fontSize:"0.95rem", color:C.textMain, marginBottom:"5px" }}>{nombre}</div>
          <table style={{ borderCollapse:"collapse", fontSize:"0.76rem", width:"100%" }}>
            <tbody>
              <tr><td style={{ ...cellS, whiteSpace:"nowrap", paddingLeft:0 }}>Cedula :</td><td style={{ ...cellL, fontWeight:600 }}>{cedula}</td></tr>
              <tr><td style={{ ...cellS, whiteSpace:"nowrap", paddingLeft:0 }}>Codigo :</td><td style={{ ...cellL }}>{codigo}</td></tr>
              <tr><td style={{ ...cellS, whiteSpace:"nowrap", paddingLeft:0 }}>Cargo. :</td><td style={{ ...cellL, fontWeight:600 }}>{cargo}</td></tr>
              <tr><td style={{ ...cellS, whiteSpace:"nowrap", paddingLeft:0 }}>Seccion:</td><td style={{ ...cellL }}>{seccion}</td></tr>
            </tbody>
          </table>
        </div>
        {/* Columna derecha */}
        <div style={{ padding:"0.6rem 0.9rem" }}>
          <div style={{ background:C.primaryLight, color:C.primary, fontSize:"0.73rem", fontWeight:700, padding:"3px 8px", borderRadius:"3px", marginBottom:"6px", display:"inline-block" }}>
            NOMINA MENSUAL &nbsp;·&nbsp; Período: {periodoStr} (Del {desdStr} al {hastaStr})
          </div>
          <table style={{ borderCollapse:"collapse", fontSize:"0.76rem", width:"100%" }}>
            <tbody>
              <tr><td style={{ ...cellS, whiteSpace:"nowrap", paddingLeft:0 }}>Fecha Ingreso.:</td><td style={{ ...cellL }}>{ingreso}</td></tr>
              <tr><td style={{ ...cellS, whiteSpace:"nowrap", paddingLeft:0 }}>Departamento:</td><td style={{ ...cellL }}>{seccion}</td></tr>
              <tr><td style={{ ...cellS, whiteSpace:"nowrap", paddingLeft:0 }}>Sueldo Mensual.:</td><td style={{ ...cellL, fontWeight:700, color:C.primary }}>{FMT(calc.salario)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── TABLA DETALLE DE PAGO ── */}
      {/* Encabezado de columnas — exacto como el original */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 70px 110px 130px", background:"#F4F6F7", borderBottom:`1px solid ${C.border}` }}>
        <div style={hdr}>Concepto</div>
        <div style={{ ...hdr, textAlign:"center" }}>Unidad</div>
        <div style={{ ...hdr, textAlign:"right" }}>Ingresos</div>
        <div style={{ ...hdr, textAlign:"right" }}>Deducciones</div>
      </div>

      {/* Fila 1: Sueldo + I.S.R. */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 70px 110px 130px", borderBottom:`1px solid ${C.neutralLight}` }}>
        <div style={cellL}>SUELDO</div>
        <div style={{ ...cellR, textAlign:"center", color:C.textSub }}>30.00</div>
        <div style={{ ...cellR, fontWeight:600 }}>{FMT(calc.salario)}</div>
        <div style={{ ...cellR, color:C.danger }}>I.S.R. &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {FMT(calc.totalISR)}</div>
      </div>
      {/* Fila 2: AFP */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 70px 110px 130px", borderBottom:`1px solid ${C.neutralLight}`, background:"#FAFAFA" }}>
        <div style={cellL}></div>
        <div style={cellR}></div>
        <div style={cellR}></div>
        <div style={{ ...cellR, color:C.danger }}>AFP &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {FMT(calc.afp)}</div>
      </div>
      {/* Fila 3: SAVICA */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 70px 110px 130px", borderBottom:`1px solid ${C.neutralLight}` }}>
        <div style={cellL}></div>
        <div style={cellR}></div>
        <div style={cellR}></div>
        <div style={{ ...cellR, color:C.danger }}>SAVICA &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {FMT(calc.savica)}</div>
      </div>
      {/* Fila 4: SFS */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 70px 110px 130px", borderBottom:`1px solid ${C.border}`, background:"#FAFAFA" }}>
        <div style={cellL}></div>
        <div style={cellR}></div>
        <div style={cellR}></div>
        <div style={{ ...cellR, color:C.danger, fontSize:"0.73rem" }}>SEG. FAMILIAR (SFS) &nbsp; {FMT(calc.sfs)}</div>
      </div>

      {/* ── TOTALES ── */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", borderTop:`2px solid ${C.border}` }}>
        {/* Total del Empleado */}
        <div style={{ padding:"0.5rem 0.9rem", borderRight:`1px solid ${C.border}` }}>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:"0.78rem" }}>
            <span style={{ fontWeight:700, color:C.textMain }}>TOTAL del Empleado</span>
            <span style={{ display:"flex", gap:"2rem" }}>
              <span style={{ fontWeight:700 }}>{FMT(calc.salario)}</span>
              <span style={{ fontWeight:700, color:C.danger }}>{FMT(calc.totalTSS + calc.totalISR)}</span>
            </span>
          </div>
          <div style={{ fontSize:"0.7rem", color:C.textSub, marginTop:"4px" }}>
            TSS: AFP {FMT(calc.afp)} + SFS {FMT(calc.sfs)} + Savica {FMT(calc.savica)} = {FMT(calc.totalTSS)} &nbsp;|&nbsp; ISR: {FMT(calc.totalISR)}
          </div>
        </div>
        {/* Neto a Pagar */}
        <div style={{ padding:"0.5rem 0.9rem", background:C.primaryLight, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontSize:"0.73rem", color:C.textSub }}>Neto a Pagar</div>
            <div style={{ fontWeight:800, color:C.primary, fontSize:"1.1rem", lineHeight:1.2 }}>{FMT(calc.salarioNeto)}</div>
          </div>
          <div style={{ textAlign:"right", fontSize:"0.7rem", color:C.textSub }}>
            <div>Recibido Conforme</div>
            <div style={{ marginTop:"14px", borderTop:`1px solid ${C.border}`, paddingTop:"2px", width:"140px" }}>&nbsp;</div>
          </div>
        </div>
      </div>

      {/* Pie */}
      <div style={{ borderTop:`1px solid ${C.neutralLight}`, padding:"4px 10px", display:"flex", justifyContent:"space-between", background:"#F9F9F9" }}>
        <span style={{ fontSize:"0.67rem", color:"#AAB7B8" }}>ProTalentHub — Sistema Integral de RRHH · ProDominicana</span>
        <span style={{ fontSize:"0.67rem", color:"#AAB7B8" }}>Generado: {new Date().toLocaleDateString("es-DO")} {new Date().toLocaleTimeString("es-DO")}</span>
      </div>
    </div>
  );
}

// ─── MODAL BOLETAS MASIVAS ────────────────────────────────────────────────────
function ModalBoletas({ nomina, config, onClose }) {
  const handlePrint = () => {
    const html = document.getElementById("area-boletas").innerHTML;
    const w = window.open("", "_blank", "width=900,height=750");
    w.document.write(`<html><head><title>Boletas ${nomina.periodo}</title>
      <style>
        body{font-family:'Courier New',Courier,monospace;margin:0;padding:16px;background:#f0f4f8;font-size:13px}
        @media print{body{background:#fff;padding:4px}div{page-break-inside:avoid}}
      </style></head><body>${html}</body></html>`);
    w.document.close(); w.focus(); w.print(); w.close();
  };

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <div style={S.mHead}>
          <div>
            <div style={{ fontWeight:700 }}>Boletas de Nómina — {nomina.periodo}</div>
            <div style={{ fontSize:"0.78rem", opacity:0.7 }}>{(nomina.empleados||[]).length} colaboradores</div>
          </div>
          <div style={{ display:"flex", gap:"0.75rem", alignItems:"center" }}>
            <button onClick={handlePrint} style={{ ...S.btn("rgba(255,255,255,0.2)"), border:"1.5px solid rgba(255,255,255,0.3)", fontSize:"0.8rem" }}>
              <Printer size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Imprimir / PDF
            </button>
            <button style={S.closeX} onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
          </div>
        </div>
        <div id="area-boletas" style={{ padding:"1.5rem", maxHeight:"78vh", overflowY:"auto" }}>
          {(nomina.empleados || []).map((emp, i) => {
            const c = emp._preCalc || calcularNomina(emp, config);
            return <Boleta key={i} emp={emp} calc={c} periodo={nomina._periodo || { mes:1, anio:new Date().getFullYear() }} />;
          })}
        </div>
      </div>
    </div>
  );
}

// ─── MODAL DETALLE INDIVIDUAL ─────────────────────────────────────────────────
function ModalDetalle({ emp, calc, periodo, onClose }) {
  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.modal, maxWidth:680 }}>
        <div style={S.mHead}>
          <div>
            <div style={{ fontWeight:700 }}>Comprobante de Pago</div>
            <div style={{ fontSize:"0.78rem", opacity:0.7 }}>{NOM(emp)}</div>
          </div>
          <button style={S.closeX} onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        </div>
        <div style={{ padding:"1.5rem" }}>
          <Boleta emp={emp} calc={calc} periodo={periodo} />
        </div>
      </div>
    </div>
  );
}

// ─── TAB: NUEVA NÓMINA ────────────────────────────────────────────────────────
function TabNueva({ config, onProcesado }) {
  const [mes,        setMes]        = useState(new Date().getMonth() + 1);
  const [anio,       setAnio]       = useState(new Date().getFullYear());
  const [emps,       setEmps]       = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [detalle,    setDetalle]    = useState(null);
  const [procesando, setProcesando] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getEmpleadosActivos()
      .then(d => setEmps(Array.isArray(d) ? d : []))
      .catch(() => setError("No se pudieron cargar los empleados."))
      .finally(() => setLoading(false));
  }, []);

  const calculos = useMemo(() => emps.map(e => ({ ...e, calc: calcularNomina(e, config) })), [emps, config]);
  const totales  = useMemo(() => calculos.reduce((acc, e) => ({
    salario:     acc.salario     + e.calc.salario,
    totalTSS:    acc.totalTSS    + e.calc.totalTSS,
    totalISR:    acc.totalISR    + e.calc.totalISR,
    salarioNeto: acc.salarioNeto + e.calc.salarioNeto,
  }), { salario:0, totalTSS:0, totalISR:0, salarioNeto:0 }), [calculos]);

  const handleProcesar = async () => {
    const periodo = `${anio}-${String(mes).padStart(2,"0")}`;
    if (!window.confirm(`¿Procesar nómina de ${MESES[mes-1]} ${anio} para ${emps.length} colaboradores?\n\nTotal Neto: ${FMT(totales.salarioNeto)}`)) return;
    setProcesando(true);
    try {
      const ids = emps.map(e => e.id || e.EmpleadoID);
      await procesarNomina({ periodo, empleadosIds: ids });
      onProcesado();
    } catch {
      alert("Error al procesar la nómina. Verifique la conexión con el servidor.");
    } finally {
      setProcesando(false);
    }
  };

  if (loading) return <Loading />;
  if (error)   return <ErrMsg m={error} />;

  return (
    <div>
      {/* Selector período + resumen rápido */}
      <div style={{ ...S.card, marginBottom:"1.25rem" }}>
        <div style={{ display:"flex", gap:"1.5rem", alignItems:"flex-end", flexWrap:"wrap" }}>
          <div style={{ flex:"0 0 190px" }}>
            <label style={S.label}>Mes</label>
            <select value={mes} onChange={e => setMes(+e.target.value)} style={S.input}>
              {MESES.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
          </div>
          <div style={{ flex:"0 0 120px" }}>
            <label style={S.label}>Año</label>
            <select value={anio} onChange={e => setAnio(+e.target.value)} style={S.input}>
              {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ flex:1, display:"flex", gap:"0.75rem", flexWrap:"wrap" }}>
            {[
              ["Salario Bruto",  FMT(totales.salario),    C.primary],
              ["Total TSS",      FMT(totales.totalTSS),   C.gold   ],
              ["Total ISR",      FMT(totales.totalISR),   C.danger ],
              ["Neto a Pagar",   FMT(totales.salarioNeto),C.primary],
            ].map(([l, v, c]) => (
              <div key={l} style={{ background:C.neutralLight, borderRadius:"8px", padding:"0.45rem 1rem", border:`1px solid ${C.border}` }}>
                <div style={{ fontSize:"0.67rem", color:C.textSub, textTransform:"uppercase" }}>{l}</div>
                <div style={{ fontWeight:700, color:c, fontSize:"0.9rem" }}>{v}</div>
              </div>
            ))}
          </div>
          <button
            onClick={handleProcesar}
            disabled={procesando || emps.length === 0}
            style={{ ...S.btn(C.primary), padding:"0.65rem 1.75rem", fontSize:"0.875rem", opacity:(procesando || emps.length===0) ? 0.6 : 1 }}
          >
            {procesando ? "Procesando..." : "Procesar Nómina"}
          </button>
        </div>
      </div>

      {/* Tabla preview */}
      <div style={S.card}>
        <div style={S.secTitle}>Vista Previa — {MESES[mes-1]} {anio} ({calculos.length} colaboradores)</div>
        {calculos.length === 0 ? (
          <div style={{ textAlign:"center", padding:"2rem", color:C.textSub }}>No hay empleados activos.</div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={S.tbl}>
              <thead>
                <tr>
                  {["Colaborador(a)","Cédula","Cargo","Sueldo Nómina","AFP 2.87%","SFS 3.04%","Savica","Total TSS","Base ISR","ISR","Neto a Pagar",""].map((h, i) => (
                    <th key={i} style={i < 3 ? S.th : S.thR}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {calculos.map((e, i) => (
                  <tr key={e.id||e.EmpleadoID||i} style={i%2===0 ? S.trAlt : {}}>
                    <td style={{ ...S.td, fontWeight:600 }}>{NOM(e)}</td>
                    <td style={S.td}>{e.cedula||e.CEDULA}</td>
                    <td style={S.td}><span style={S.badge(C.primary,C.primaryLight)}>{(e.cargo||e.CARGO||"").slice(0,26)}</span></td>
                    <td style={S.tdR}>{FMT(e.calc.salario)}</td>
                    <td style={S.tdR}>{FMT(e.calc.afp)}</td>
                    <td style={S.tdR}>{FMT(e.calc.sfs)}</td>
                    <td style={S.tdR}>{FMT(e.calc.savica)}</td>
                    <td style={{ ...S.tdR, color:C.gold,   fontWeight:700 }}>{FMT(e.calc.totalTSS)}</td>
                    <td style={S.tdR}>{FMT(e.calc.baseISR)}</td>
                    <td style={{ ...S.tdR, color:C.danger, fontWeight:700 }}>{FMT(e.calc.totalISR)}</td>
                    <td style={S.tdB}>{FMT(e.calc.salarioNeto)}</td>
                    <td style={{ padding:"9px 8px", borderBottom:`1px solid ${C.neutralLight}`, textAlign:"center" }}>
                      <button
                        onClick={() => setDetalle(e)}
                        style={{ background:C.primaryLight, border:"none", borderRadius:"5px", padding:"3px 10px", fontSize:"0.72rem", color:C.primary, cursor:"pointer", fontWeight:600 }}
                      >Ver</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background:C.primaryDark }}>
                  <td colSpan={3} style={{ ...S.td, color:C.white, fontWeight:700, borderBottom:"none" }}>TOTALES</td>
                  <td style={{ ...S.tdR, color:C.white,   fontWeight:700, borderBottom:"none" }}>{FMT(totales.salario)}</td>
                  <td style={{ ...S.tdR, color:"#A9DFBF", borderBottom:"none" }}>{FMT(calculos.reduce((a,e)=>a+e.calc.afp,0))}</td>
                  <td style={{ ...S.tdR, color:"#A9DFBF", borderBottom:"none" }}>{FMT(calculos.reduce((a,e)=>a+e.calc.sfs,0))}</td>
                  <td style={{ ...S.tdR, color:"#A9DFBF", borderBottom:"none" }}>{FMT(calculos.reduce((a,e)=>a+e.calc.savica,0))}</td>
                  <td style={{ ...S.tdR, color:C.gold,    fontWeight:700, borderBottom:"none" }}>{FMT(totales.totalTSS)}</td>
                  <td style={{ ...S.tdR, color:"#D5D8DC", borderBottom:"none" }}>{FMT(calculos.reduce((a,e)=>a+e.calc.baseISR,0))}</td>
                  <td style={{ ...S.tdR, color:"#F1948A", fontWeight:700, borderBottom:"none" }}>{FMT(totales.totalISR)}</td>
                  <td style={{ ...S.tdR, color:"#A9DFBF", fontWeight:800, fontSize:"0.93rem", borderBottom:"none" }}>{FMT(totales.salarioNeto)}</td>
                  <td style={{ borderBottom:"none" }} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {detalle && (
        <ModalDetalle emp={detalle} calc={detalle.calc} periodo={{ mes, anio }} onClose={() => setDetalle(null)} />
      )}
    </div>
  );
}

// ─── TAB: HISTORIAL ───────────────────────────────────────────────────────────
function TabDashboard({ config, onNuevaTab }) {
  const [nominas, setNominas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [modal,   setModal]   = useState(null);
  const [mes,     setMes]     = useState(new Date().getMonth() + 1);
  const [anio,    setAnio]    = useState(new Date().getFullYear());

  const cargar = useCallback(() => {
    setLoading(true);
    setError(null);
    const periodo = `${anio}-${String(mes).padStart(2,"0")}`;
    getNominas({ periodo })
      .then(d => setNominas(Array.isArray(d) ? d : []))
      .catch(() => setError("No se pudo cargar el historial."))
      .finally(() => setLoading(false));
  }, [mes, anio]);

  useEffect(() => { cargar(); }, [cargar]);

  const totales = useMemo(() => nominas.reduce((a, n) => ({
    bruto: a.bruto + (n.SalarioBase||0),
    tss:   a.tss   + (n.TotalTSS||0),
    isr:   a.isr   + (n.ISR||0),
    neto:  a.neto  + (n.SalarioNeto||0),
  }), { bruto:0, tss:0, isr:0, neto:0 }), [nominas]);

  const buildModal = () => ({
    periodo:  `${MESES[mes-1]} ${anio}`,
    _periodo: { mes, anio },
    empleados: nominas.map(n => ({
      NOMBRE: n.NOMBRE, APELLIDO: n.APELLIDO, nombre: n.NOMBRE, apellidos: n.APELLIDO,
      CEDULA: n.CEDULA, CARGO: n.CARGO,
      FECHAINGRESO: n.FECHAINGRESO,
      departamento: n.Departamento || "",
      id: n.EmpleadoID,
      salarioBase: n.SalarioBase,
      // Usar valores calculados por la BD directamente en la boleta
      _preCalc: {
        salario:     n.SalarioBase,
        afp:         n.TSS_AFP,
        sfs:         n.TSS_SFS,
        savica:      n.Savica ?? 25,
        totalTSS:    n.TotalTSS,
        baseISR:     n.SalarioBase - (n.TSS_AFP||0) - (n.TSS_SFS||0),
        totalISR:    n.ISR,
        tramos:      [],
        salarioNeto: n.SalarioNeto,
        patronalAfp:0, patronalSfs:0, patronalRiesgo:0, infotep:0, totalPatronal:0,
      },
    })),
  });

  return (
    <div>
      {/* Filtro */}
      <div style={{ ...S.card, marginBottom:"1.25rem" }}>
        <div style={{ display:"flex", gap:"1rem", alignItems:"flex-end", flexWrap:"wrap" }}>
          <div style={{ flex:"0 0 190px" }}>
            <label style={S.label}>Mes</label>
            <select value={mes} onChange={e => setMes(+e.target.value)} style={S.input}>
              {MESES.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
          </div>
          <div style={{ flex:"0 0 120px" }}>
            <label style={S.label}>Año</label>
            <select value={anio} onChange={e => setAnio(+e.target.value)} style={S.input}>
              {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button onClick={cargar} style={{ ...S.btn(C.primary), padding:"0.6rem 1.25rem" }}>Buscar</button>
        </div>
      </div>

      {/* Stats */}
      <div style={S.grid4}>
        {[
          ["Registros",   nominas.length,         C.primary, v => v],
          ["Total Bruto", totales.bruto,           "#2874A6", FMT  ],
          ["TSS + ISR",   totales.tss+totales.isr, C.gold,    FMT  ],
          ["Neto Pagado", totales.neto,            C.success, FMT  ],
        ].map(([l, v, c, f]) => (
          <div key={l} style={S.stat(c)}>
            <div style={{ fontWeight:700, color:c, fontSize:"1.5rem", lineHeight:1.1 }}>{f(v)}</div>
            <div style={{ fontSize:"0.75rem", color:C.textSub, marginTop:"4px" }}>{l}</div>
          </div>
        ))}
      </div>

      {loading ? <Loading /> : error ? <ErrMsg m={error} /> : nominas.length === 0 ? (
        <div style={{ ...S.card, textAlign:"center", padding:"3rem", color:C.textSub }}>
          <div style={{ marginBottom:"0.75rem", color:"#94a3b8" }}><ClipboardList size={32} /></div>
          <div style={{ fontWeight:600, marginBottom:"0.75rem" }}>Sin nóminas para {MESES[mes-1]} {anio}</div>
          <button onClick={onNuevaTab} style={{ ...S.btn(C.primary), display:"inline-flex" }}>Procesar Nueva Nómina</button>
        </div>
      ) : (
        <div style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1rem" }}>
            <div style={S.secTitle}>{MESES[mes-1]} {anio} — {nominas.length} colaboradores</div>
            <button onClick={() => setModal(buildModal())} style={{ ...S.btn(C.primary), padding:"0.5rem 1.1rem", fontSize:"0.8rem" }}>
              Ver Boletas del Período
            </button>
          </div>
          <div style={{ overflowX:"auto" }}>
            <table style={S.tbl}>
              <thead>
                <tr>
                  {["Colaborador(a)","Cédula","Cargo","Sueldo Nómina","AFP","SFS","Savica","Total TSS","ISR","Neto a Pagar","Estado"].map((h, i) => (
                    <th key={i} style={i < 3 ? S.th : S.thR}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {nominas.map((n, i) => (
                  <tr key={n.NominaID||i} style={i%2===0 ? S.trAlt : {}}>
                    <td style={{ ...S.td, fontWeight:600 }}>{NOM(n)}</td>
                    <td style={S.td}>{n.CEDULA}</td>
                    <td style={S.td}>{n.CARGO}</td>
                    <td style={S.tdR}>{FMT(n.SalarioBase)}</td>
                    <td style={S.tdR}>{FMT(n.TSS_AFP)}</td>
                    <td style={S.tdR}>{FMT(n.TSS_SFS)}</td>
                    <td style={S.tdR}>{FMT(n.Savica??25)}</td>
                    <td style={{ ...S.tdR, color:C.gold,   fontWeight:700 }}>{FMT(n.TotalTSS)}</td>
                    <td style={{ ...S.tdR, color:C.danger, fontWeight:700 }}>{FMT(n.ISR)}</td>
                    <td style={S.tdB}>{FMT(n.SalarioNeto)}</td>
                    <td style={S.td}><span style={S.badge(C.success,C.successLight)}>{n.Estado}</span></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background:C.primaryDark }}>
                  <td colSpan={3} style={{ ...S.td, color:C.white, fontWeight:700, borderBottom:"none" }}>TOTALES</td>
                  <td style={{ ...S.tdR, color:C.white, fontWeight:700, borderBottom:"none" }}>{FMT(totales.bruto)}</td>
                  <td style={{ ...S.tdR, color:"#A9DFBF", borderBottom:"none" }}>{FMT(nominas.reduce((a,n)=>a+(n.TSS_AFP||0),0))}</td>
                  <td style={{ ...S.tdR, color:"#A9DFBF", borderBottom:"none" }}>{FMT(nominas.reduce((a,n)=>a+(n.TSS_SFS||0),0))}</td>
                  <td style={{ ...S.tdR, color:"#A9DFBF", borderBottom:"none" }}>{FMT(nominas.reduce((a,n)=>a+(n.Savica??25),0))}</td>
                  <td style={{ ...S.tdR, color:C.gold,    fontWeight:700, borderBottom:"none" }}>{FMT(totales.tss)}</td>
                  <td style={{ ...S.tdR, color:"#F1948A", fontWeight:700, borderBottom:"none" }}>{FMT(totales.isr)}</td>
                  <td style={{ ...S.tdR, color:"#A9DFBF", fontWeight:800, borderBottom:"none" }}>{FMT(totales.neto)}</td>
                  <td style={{ borderBottom:"none" }} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {modal && <ModalBoletas nomina={modal} config={config} onClose={() => setModal(null)} />}
    </div>
  );
}

// ─── TAB: CONFIGURACIÓN ───────────────────────────────────────────────────────
function TabConfig({ onConfigSaved }) {
  const [draft,   setDraft]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    getConfiguracion()
      .then(data => setDraft({
        escalasISR:        data.filter(c => c.TipoConfiguracion === "ISR"           ).sort((a,b)=>a.LimiteInferior-b.LimiteInferior),
        deducciones:       data.filter(c => c.TipoConfiguracion === "DEDUCCION"     ),
        aportesPatronales: data.filter(c => c.TipoConfiguracion === "APORTE_PATRONAL"),
      }))
      .catch(() => setError("No se pudo cargar la configuración."))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const cfgs = [
        ...(draft.escalasISR||[]).map(e => ({ codigo:e.Codigo, porcentaje:e.Porcentaje, limiteInferior:e.LimiteInferior, limiteSuperior:e.LimiteSuperior, montoFijo:e.MontoFijo })),
        ...(draft.deducciones||[]).map(e => ({ codigo:e.Codigo, porcentaje:e.Porcentaje })),
        ...(draft.aportesPatronales||[]).map(e => ({ codigo:e.Codigo, porcentaje:e.Porcentaje })),
      ];
      await actualizarConfiguracion(cfgs);
      if (onConfigSaved) onConfigSaved(draft);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      alert("Error al guardar la configuración.");
    } finally {
      setSaving(false);
    }
  };

  const upISR = (id, f, v) => setDraft(d => ({ ...d, escalasISR: d.escalasISR.map(e => e.ConfigID===id ? { ...e,[f]:parseFloat(v)||0 } : e) }));
  const upDed = (id, v)    => setDraft(d => ({ ...d, deducciones: d.deducciones.map(e => e.ConfigID===id ? { ...e, Porcentaje:parseFloat(v)||0 } : e) }));
  const upPat = (id, v)    => setDraft(d => ({ ...d, aportesPatronales: d.aportesPatronales.map(e => e.ConfigID===id ? { ...e, Porcentaje:parseFloat(v)||0 } : e) }));

  if (loading) return <Loading />;
  if (error)   return <ErrMsg m={error} />;
  if (!draft)  return null;

  const totalDed = (draft.deducciones||[]).reduce((a,e)=>a+e.Porcentaje,0);
  const totalPat = (draft.aportesPatronales||[]).reduce((a,e)=>a+e.Porcentaje,0);

  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1.25rem" }}>

      {/* ISR */}
      <div style={S.card}>
        <div style={S.secTitle}>Tabla ISR — DGII</div>
        <table style={S.tbl}>
          <thead>
            <tr>
              {["#","Límite Inf.","Límite Sup.","Tasa %","Monto Fijo (ref.)"].map((h,i)=>(
                <th key={i} style={i<2?S.th:S.thR}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(draft.escalasISR||[]).map((e, i) => (
              <tr key={e.ConfigID} style={i%2===0?S.trAlt:{}}>
                <td style={S.td}>{i+1}</td>
                <td style={S.tdR}>{FMT(e.LimiteInferior)}</td>
                <td style={S.tdR}>{(e.LimiteSuperior||0)>=999999999?"—":FMT(e.LimiteSuperior)}</td>
                <td style={{ ...S.td, padding:"5px 8px" }}>
                  <input type="number" value={e.Porcentaje||0} onChange={ev=>upISR(e.ConfigID,"Porcentaje",ev.target.value)} style={{ ...S.inputR, width:68 }} step="0.1" min="0" max="100" />
                </td>
                <td style={{ ...S.td, padding:"5px 8px" }}>
                  <input type="number" value={e.MontoFijo||0} onChange={ev=>upISR(e.ConfigID,"MontoFijo",ev.target.value)} style={{ ...S.inputR, width:108 }} step="0.01" min="0" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop:"0.75rem", background:C.primaryLight, borderRadius:"6px", padding:"0.55rem 0.75rem", fontSize:"0.75rem", color:C.primary, lineHeight:1.5 }}>
          <strong>Cálculo real:</strong> ISR progresivo por tramos acumulados.<br/>
          Base ISR = Salario − AFP − SFS &nbsp;(Savica excluido).<br/>
          El Monto Fijo es solo referencial para la tabla DGII.
        </div>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:"1.25rem" }}>
        {/* Cuotas TSS Empleado */}
        <div style={S.card}>
          <div style={S.secTitle}>Cuotas TSS — Empleado</div>
          <table style={S.tbl}>
            <thead><tr><th style={S.th}>Descripción</th><th style={S.thR}>Valor</th><th style={S.thR}>Tipo</th></tr></thead>
            <tbody>
              {(draft.deducciones||[]).map((e, i) => (
                <tr key={e.ConfigID} style={i%2===0?S.trAlt:{}}>
                  <td style={S.td}>{e.Nombre}</td>
                  <td style={{ ...S.td, padding:"5px 8px", textAlign:"right" }}>
                    <input type="number" value={e.Porcentaje||0} onChange={ev=>upDed(e.ConfigID,ev.target.value)} style={{ ...S.inputR, width:88 }} step="0.01" min="0" />
                  </td>
                  <td style={{ ...S.tdR, fontSize:"0.72rem", color:C.textSub }}>
                    {e.Codigo==="SAVICA" ? "RD$ fijo" : "% salario"}
                  </td>
                </tr>
              ))}
              <tr style={{ background:C.primaryLight }}>
                <td style={{ ...S.td, fontWeight:700, color:C.primary }}>Total TSS Empleado</td>
                <td style={{ ...S.tdR, fontWeight:800, color:C.primary }}>{PCT(totalDed)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>

        {/* Aportes Patronales */}
        <div style={S.card}>
          <div style={S.secTitle}>Aportes Patronales</div>
          <table style={S.tbl}>
            <thead><tr><th style={S.th}>Descripción</th><th style={S.thR}>Porcentaje</th></tr></thead>
            <tbody>
              {(draft.aportesPatronales||[]).map((e, i) => (
                <tr key={e.ConfigID} style={i%2===0?S.trAlt:{}}>
                  <td style={S.td}>{e.Nombre}</td>
                  <td style={{ ...S.td, padding:"5px 8px", textAlign:"right" }}>
                    <input type="number" value={e.Porcentaje||0} onChange={ev=>upPat(e.ConfigID,ev.target.value)} style={{ ...S.inputR, width:88 }} step="0.01" min="0" />
                  </td>
                </tr>
              ))}
              <tr style={{ background:"#F4ECF7" }}>
                <td style={{ ...S.td, fontWeight:700, color:"#6C3483" }}>Total Patronal</td>
                <td style={{ ...S.tdR, fontWeight:800, color:"#6C3483" }}>{PCT(totalPat)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Botones */}
      <div style={{ gridColumn:"1/-1", display:"flex", gap:"1rem", alignItems:"center", flexWrap:"wrap" }}>
        <button onClick={handleSave} disabled={saving} style={{ ...S.btn(C.primary), padding:"0.65rem 2rem", opacity:saving?0.7:1 }}>
          {saving?"Guardando...":saved?"Guardado":"Guardar Cambios"}
        </button>
        <div style={{ background:C.warningLight, borderRadius:"8px", padding:"0.55rem 1rem", border:`1px solid ${C.warning}`, fontSize:"0.78rem", color:"#7D6608" }}>
          Los cambios afectan todos los cálculos futuros. Verifique los valores oficiales de la DGII antes de guardar.
        </div>
      </div>
    </div>
  );
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
export default function ModuloNomina() {
  const [tab,    setTab]    = useState("nueva");
  const [config, setConfig] = useState({ escalasISR:[], deducciones:[], aportesPatronales:[] });

  // Cargar config al inicio para usarla en los cálculos del preview
  useEffect(() => {
    getConfiguracion()
      .then(data => setConfig({
        escalasISR:        data.filter(c=>c.TipoConfiguracion==="ISR"           ).sort((a,b)=>a.LimiteInferior-b.LimiteInferior),
        deducciones:       data.filter(c=>c.TipoConfiguracion==="DEDUCCION"     ),
        aportesPatronales: data.filter(c=>c.TipoConfiguracion==="APORTE_PATRONAL"),
      }))
      .catch(() => {/* calcularNomina usa fallbacks con valores reales DGII */});
  }, []);

  const TABS = [
    { id:"nueva",     label:"Nueva Nómina"  },
    { id:"dashboard", label:"Historial"     },
    { id:"config",    label:"Configuración" },
  ];

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <p style={{ color:C.white, fontSize:"1.15rem", fontWeight:700, margin:0 }}>Módulo de Nómina</p>
          <p style={{ color:"rgba(255,255,255,0.6)", fontSize:"0.75rem", margin:0, marginTop:"2px" }}>
            ProTalentHub · Centro de Exportación e Inversión de la R.D. (CEI-RD)
          </p>
        </div>
        <div style={{ color:C.gold, fontWeight:700, fontSize:"0.85rem" }}>Sistema Operativo</div>
      </div>

      {/* Pestañas */}
      <div style={S.tabs}>
        {TABS.map(t => (
          <button key={t.id} style={S.tab(tab===t.id)} onClick={()=>setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* Contenido */}
      <div style={S.body}>
        {tab==="nueva"     && <TabNueva     config={config} onProcesado={()=>setTab("dashboard")} />}
        {tab==="dashboard" && <TabDashboard config={config} onNuevaTab={()=>setTab("nueva")} />}
        {tab==="config"    && <TabConfig    onConfigSaved={setConfig} />}
      </div>
    </div>
  );
}